/**
 * Where a header dropdown opens so it is ALWAYS fully on screen (WCAG 2.2 SC
 * 1.4.10 Reflow; cca-69gs, moved here from the parish picker in cca-5weu).
 *
 * Since PortalHeader wraps, no control in its slot is guaranteed to be the
 * right-most one: at 390px the parish picker can sit at the LEFT of a line of
 * three pills, where a list hung from its right edge opens past the left edge of
 * the screen. One function, so every header dropdown (the parish list, the
 * Sign out menu) answers that the same way (ARCH-11).
 *
 * Measured when the dropdown opens, against the visible viewport width:
 *   • `prefer: 'left'` — hang from the trigger's LEFT edge (`left: 0`) when there
 *     is room to its right; otherwise from its RIGHT edge; if neither fits, push
 *     it in so it starts `edge` from the LEFT of the screen.
 *   • `prefer: 'right'` — the mirror: the RIGHT edge first (the old `right: 0`
 *     placement), then the left edge, and if neither fits it is pulled in so it
 *     ends `edge` from the RIGHT of the screen.
 * The width is `width`, capped at the viewport less both edges, so one of the
 * three always fits. Pure, so it is tested without a browser.
 */

/** The trigger's horizontal extent — a `DOMRect` satisfies it. */
export interface DOMRectLike {
  left: number;
  right: number;
}

export interface DropdownPlacement {
  /** Which edge of the trigger the dropdown hangs from (`clamped`: neither fits,
   *  so it is pushed in from the screen edge on its preferred side). */
  anchor: 'left' | 'right' | 'clamped';
  /** CSS `left` in px, relative to the trigger's own left edge. */
  left: number;
  /** CSS `width` in px — a BORDER-BOX width, so style the dropdown
   *  `boxSizing: 'border-box'` or the arithmetic is short by its border and padding. */
  width: number;
}

export interface DropdownOptions {
  /** The dropdown's preferred width in px. */
  width: number;
  /** The clear space kept between the dropdown and either screen edge, in px. */
  edge?: number;
  /** Which trigger edge to hang from first. */
  prefer: 'left' | 'right';
}

/** The default clear space between an open dropdown and either screen edge. */
export const DROPDOWN_EDGE = 16;

export function placeDropdown(
  trigger: DOMRectLike,
  vw: number,
  { width, edge = DROPDOWN_EDGE, prefer }: DropdownOptions,
): DropdownPlacement {
  const w = Math.max(0, Math.min(width, vw - 2 * edge));
  const leftHung = (): DropdownPlacement | null =>
    trigger.left + w <= vw - edge ? { anchor: 'left', left: 0, width: w } : null;
  const rightHung = (): DropdownPlacement | null => {
    const at = trigger.right - w;
    return at >= edge ? { anchor: 'right', left: at - trigger.left, width: w } : null;
  };
  if (prefer === 'left') {
    return leftHung() ?? rightHung() ?? { anchor: 'clamped', left: edge - trigger.left, width: w };
  }
  return (
    rightHung() ?? leftHung() ?? { anchor: 'clamped', left: vw - edge - w - trigger.left, width: w }
  );
}
